import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Card,
  Theme,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { DropzoneAreaBase, FileObject } from 'material-ui-dropzone';
import { MdClose } from 'react-icons/md';
import { anexarDocumentoSolicitado } from '../../services/solicitacaoDocumento';
import { useToastValue } from '../../contexts/toastMessage';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  solicitacaoDocumentoId: string;
  descricao?: string;
  documentoModeloUrl?: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    background: theme.palette.primary.main,
    color: '#fff',
    padding: '16px 24px',
  },
  textHeader: {
    display: 'flex',
    alignItems: 'center',
    textTransform: 'uppercase',
    fontFamily: 'Roboto',
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
  },
  buttonClose: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(0.5),
    color: '#fff',
  },
  icon: {
    width: 24,
    height: 24,
  },
  content: {
    padding: '24px',
    fontFamily: 'Roboto',
  },
  title: {
    fontSize: theme.typography.pxToRem(16),
    color: '#373F41',
    marginTop: 20,
    marginBottom: 10,
    fontWeight: 600,
    fontFamily: 'Roboto',
  },
  text: {
    fontSize: theme.typography.pxToRem(14),
    color: '#373F41',
    marginBottom: 16,
    fontFamily: 'Roboto',
    fontWeight: 400,
    lineHeight: '20px',
  },
  rootUpload: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 140,
    flexDirection: 'column',
    border: '1px solid #E1E6EF',
    borderRadius: 4,
    marginTop: 16,
  },
  iconUpload: {
    color: '#9E9E9E',
  },
  card: {
    width: '100%',
    color: '#373F41',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    marginTop: 8,
    border: '1px solid #EAECF0',
    boxShadow: '0px 8px 16px rgba(11, 31, 77, 0.04)',
    borderRadius: 6,
    fontSize: 16,
    fontWeight: 400,
    fontFamily: 'Roboto',
  },
  buttonDownload: {
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
    fontFamily: 'Roboto',
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(14),
    marginTop: 8,
    marginBottom: 16,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  buttonRemove: {
    textTransform: 'uppercase',
    color: '#F03D3D',
    fontFamily: 'Roboto',
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(14),
    marginTop: 8,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  containerDialogActions: {
    display: 'flex',
    margin: '0px 24px 24px 24px',
    justifyContent: 'flex-end',
  },
  buttonSend: {
    textTransform: 'uppercase',
    color: '#fff',
    borderRadius: 4,
    fontFamily: 'Roboto',
    fontWeight: 500,
    fontSize: theme.typography.pxToRem(15),
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: 'rgba(15, 111, 183, 0.7)',
    },
  },
  buttonCancel: {
    textTransform: 'uppercase',
    color: theme.palette.primary.main,
    fontFamily: 'Roboto',
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(14),
    marginRight: 16,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  infoText: {
    fontFamily: 'Roboto',
    fontSize: 12,
    fontWeight: 400,
    color: 'rgba(55, 63, 65, 0.6)',
    marginTop: 8,
  },
  circularProgress: {
    width: 25,
    height: 25,
  },
}));

const ModalAnexarDocumento: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  solicitacaoDocumentoId,
  descricao,
  documentoModeloUrl,
}) => {
  const classes = useStyles();
  const { setToast } = useToastValue();
  const [files, setFiles] = useState<FileObject[]>([]);
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (files.length === 0) {
      setToast({
        visible: true,
        type: 'error',
        message: 'Por favor, selecione um arquivo para anexar.',
      });
      return;
    }

    try {
      setLoading(true);
      await anexarDocumentoSolicitado(solicitacaoDocumentoId, files[0].file);
      
      setFiles([]);
      setToast({
        visible: true,
        type: 'success',
        message: 'Documento anexado com sucesso!',
      });
      onSuccess();
      onClose();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.detail || 
                          error.message || 
                          'Erro ao anexar documento. Tente novamente.';
      setToast({
        visible: true,
        type: 'error',
        message: errorMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    onClose();
  };

  const removeFile = () => {
    setFiles([]);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <IconButton onClick={handleClose} className={classes.buttonClose}>
        <MdClose className={classes.icon} />
      </IconButton>
      <DialogTitle className={classes.header}>
        <Typography className={classes.textHeader}>
          Anexar Documento
        </Typography>
      </DialogTitle>
      <DialogContent className={classes.content}>
        {documentoModeloUrl && (
          <Box>
            <Typography className={classes.title}>
              Documento para assinatura:
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              href={documentoModeloUrl}
              target="_blank"
              className={classes.buttonDownload}
            >
              Baixar documento modelo
            </Button>
          </Box>
        )}
        {descricao && (
          <Box marginTop={documentoModeloUrl ? 0 : 2}>
            <Typography className={classes.title}>
              Descrição:
            </Typography>
            <Typography className={classes.text}>
              {descricao}
            </Typography>
          </Box>
        )}
        <Box marginTop={2}>
          <Typography className={classes.title}>
            Anexe o documento solicitado:
          </Typography>
          {files.length === 0 ? (
            <>
              <DropzoneAreaBase
                onAdd={(filesUpload) => setFiles(filesUpload)}
                fileObjects={files}
                acceptedFiles={['application/pdf', 'image/png', 'image/jpeg']}
                showPreviewsInDropzone={false}
                dropzoneText="Faça upload ou solte arquivos aqui"
                showFileNames={false}
                showFileNamesInPreview={false}
                showAlerts={false}
                filesLimit={1}
                maxFileSize={10485760}
                classes={{
                  icon: classes.iconUpload,
                  root: classes.rootUpload,
                }}
              />
              <Typography className={classes.infoText}>
                Tamanho máximo: 10MB. Formatos aceitos: PDF, PNG, JPEG
              </Typography>
            </>
          ) : (
            <Card className={classes.card}>
              <Typography>
                {files[0].file.name} ({(files[0].file.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
              <IconButton onClick={removeFile} size="small">
                <MdClose className={classes.icon} style={{ color: '#333' }} />
              </IconButton>
            </Card>
          )}
        </Box>
      </DialogContent>
      <DialogActions className={classes.containerDialogActions}>
        <Button onClick={handleClose} disabled={loading} className={classes.buttonCancel}>
          Cancelar
        </Button>
        {loading ? (
          <CircularProgress className={classes.circularProgress} />
        ) : (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={files.length === 0}
            className={classes.buttonSend}
          >
            Enviar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ModalAnexarDocumento;
